<template>
  <div>
    <material-button
      class="d-none"
      data-bs-toggle="modal"
      data-bs-target="#modalPromoCodeSchedules"
      id="show-modal-promo-code-schedules"
    ></material-button>
    <div class="modal fade" id="modalPromoCodeSchedules" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Promo Code Schedule</h5>
            <i
              class="material-icons btn-close-modal justify-content-end cursor-pointer"
              data-bs-dismiss="modal"
              aria-label="Close"
              >clear</i
            >
          </div>
          <div class="modal-body">
            <div class="ps-master">
              <div>
                <div class="ps-master__title">Only usable during schedules</div>
                <div class="ps-master__sub">
                  When off, the code works at any time and the schedules below are kept.
                </div>
              </div>
              <div class="form-check form-switch mb-0 ms-auto">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="promo_schedules_enabled"
                  :checked="enabled"
                  @change="setEnabled($event.target.checked)"
                />
                <label class="form-check-label mb-0" for="promo_schedules_enabled">
                  {{ enabled ? "On" : "Off" }}
                </label>
              </div>
            </div>

            <div class="ps-hint">
              <i class="material-icons ps-hint__icon">info</i>
              <div>
                While on, customers can apply this code on MOO and KIOSK only inside a switched-on
                schedule (its dates, days and hours, Singapore time GMT+8); overlapping schedules
                simply add up. With no schedule switched on, the code works at any time. Status,
                expiry date, quantity and branches still apply.
              </div>
            </div>

            <div v-if="saveError" class="alert alert-danger text-white py-2 mb-3 ps-error">
              {{ saveError }}
            </div>

            <div v-if="!rows.length" class="ps-empty">No schedules yet.</div>

            <div :class="{ 'ps-list--off': !enabled }">
            <div
              v-for="(schedule, index) in rows"
              :key="index"
              class="ps-card"
              :class="{ 'ps-card--off': !schedule.status, 'ps-card--invalid': errors[index] }"
            >
              <div class="ps-card__head">
                <span class="ps-card__title">Schedule {{ index + 1 }}</span>
                <span v-if="schedule.ended" class="ps-card__ended" title="This period is over">Ended</span>
                <div class="form-check form-switch mb-0 ms-auto">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="'promo_schedule_status_' + index"
                    :checked="!!schedule.status"
                    @change="schedule.status = $event.target.checked ? 1 : 0"
                  />
                  <label class="form-check-label mb-0" :for="'promo_schedule_status_' + index">
                    {{ schedule.status ? "On" : "Off" }}
                  </label>
                </div>
                <i
                  class="material-icons ps-card__remove"
                  title="Remove schedule"
                  @click="rows.splice(index, 1)"
                  >delete_outline</i
                >
              </div>

              <div class="ps-card__body">
                <ScheduleWhenFields
                  :schedule="schedule"
                  :id-prefix="'promo_schedule_' + index"
                  @update="Object.assign(schedule, $event, { ended: false })"
                />

                <div class="text-danger ps-error" v-if="errors[index]">{{ errors[index] }}</div>
              </div>
            </div>

            <button
              type="button"
              class="ps-add"
              :disabled="rows.length >= max"
              @click="addRow"
            >
              <i class="material-icons">add</i> Add schedule
            </button>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" data-bs-dismiss="modal">
              Cancel
            </button>
            <material-button class="btn btn-sm btn-background mb-0" :disabled="saving" @click="apply">
              {{ saving ? "Saving..." : "Save" }}
            </material-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MaterialButton from "@/components/MaterialButton.vue";
import SettingPromoCodeService from "../services/settingPromoCode.service";
// Same period format and limit as Special Scheduled Price.
import { newScheduleWhen, scheduleWhenError, scheduleWhenFrom, scheduleWhenPayload, PRICE_SCHEDULE_MAX } from "@/config/priceSchedule";
import ScheduleWhenFields from "@/components/ScheduleWhenFields.vue";

// Promo code schedule editor (switch + periods in which the code can be used), built on
// ModalPriceSchedules. Works on a copy: Save checks it (same rules as the API) and saves it
// straight away for this promo; Cancel drops it.
export default {
  name: "ModalPromoCodeSchedules",
  components: { MaterialButton, ScheduleWhenFields },
  props: {
    promoCodeId: { type: [Number, String], default: 0 },
  },
  emits: ["saved"],
  data() {
    return {
      rows: [],
      // Switch (minty_promo_codes.schedules_enabled), saved together with the rows.
      enabled: false,
      // Incomplete rows are flagged only after Apply; clashes show right away.
      checked: false,
      max: PRICE_SCHEDULE_MAX,
      saving: false,
      saveError: "",
    };
  },
  computed: {
    // One message (or "") per row. Schedules may overlap: the code works in any of them.
    errors() {
      return this.rows.map((schedule) => scheduleWhenError(schedule));
    },
  },
  methods: {
    open(schedules, enabled) {
      this.enabled = Number(enabled) === 1;
      this.rows = (schedules || []).map((schedule) => ({ ...schedule, ...scheduleWhenFrom(schedule) }));
      this.checked = false;
      this.saveError = "";
      document.getElementById("show-modal-promo-code-schedules").click();
    },
    // Switching on with nothing listed starts a row to fill in.
    setEnabled(checked) {
      this.enabled = checked;
      if (checked && !this.rows.length) {
        this.addRow();
      }
    },
    addRow() {
      this.rows.push({ ...newScheduleWhen(), status: 1 });
    },
    apply() {
      this.checked = true;
      this.saveError = "";
      if (this.errors.some(Boolean)) {
        return;
      }
      this.saving = true;
      SettingPromoCodeService.saveSchedules(
        this.promoCodeId,
        this.rows.map((schedule) => ({
          ...scheduleWhenPayload(schedule),
          status: schedule.status ? 1 : 0,
        })),
        this.enabled ? 1 : 0,
      ).then(
        (response) => {
          this.saving = false;
          if (response.data.result.isSuccess) {
            this.$emit("saved", response.data.data);
            document.querySelector("#modalPromoCodeSchedules .btn-close-modal").click();
          } else {
            this.saveError = response.data.result.message;
          }
        },
        (error) => {
          this.saving = false;
          this.saveError = error?.response?.data?.result?.message || error.message;
        },
      );
    },
  },
};
</script>

<style scoped>
.ps-master {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.ps-master__title {
  font-weight: 600;
  color: #344767;
}
.ps-master__sub {
  font-size: 13px;
  color: #7b809a;
}
.ps-list--off {
  opacity: 0.6;
}
.ps-hint {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  background: #f4f6f9;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 14px;
  font-size: 13px;
  line-height: 1.45;
  color: #67748e;
}
.ps-hint__icon {
  font-size: 18px;
  color: #8a94a6;
}
.ps-empty {
  font-size: 14px;
  color: #7b809a;
  text-align: center;
  padding: 18px 0 8px;
}
.ps-error {
  font-size: 13px;
}
.ps-card {
  border: 1px solid #e3e6ea;
  border-radius: 10px;
  margin-bottom: 12px;
  overflow: visible;
}
.ps-card--off {
  background: #f8f9fa;
}
.ps-card--off .ps-card__body {
  opacity: 0.55;
}
.ps-card--invalid {
  border-color: #f44335;
}
.ps-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f3;
}
.ps-card__title {
  font-weight: 600;
  color: #344767;
}
.ps-card__ended {
  padding: 2px 8px;
  border-radius: 10px;
  background: #fff3e0;
  color: #e65100;
  font-size: 11px;
  font-weight: 600;
}
.ps-card__remove {
  cursor: pointer;
  color: #9aa0ac;
  font-size: 20px;
}
.ps-card__remove:hover {
  color: #f44335;
}
.ps-card__body {
  padding: 12px 14px 14px;
  display: grid;
  gap: 14px;
}
.ps-field {
  display: grid;
  gap: 6px;
  min-width: 0;
}
.ps-field__label {
  font-size: 12px;
  font-weight: 600;
  color: #7b809a;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.ps-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}
.ps-datetime {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px;
  gap: 8px;
}
.ps-date :deep(.dp__input) {
  font-size: 14px;
  color: #344767;
  height: 40px;
}
@media (max-width: 767px) {
  .ps-grid {
    grid-template-columns: 1fr;
  }
}
.ps-add {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px dashed #c4cad3;
  border-radius: 10px;
  background: transparent;
  color: #344767;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  justify-content: center;
  padding: 10px;
}
.ps-add:hover:not(:disabled) {
  background: #f4f6f9;
}
.ps-add:disabled {
  opacity: 0.5;
}
.ps-add .material-icons {
  font-size: 18px;
}
</style>
